import { useCallback, useMemo } from 'react';

import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import { ColorStyleInput } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import { SectionInputWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import { StyleValueColorConfig } from 'lib/inquiryStyles';
import {
  ColorComponent,
  ColorComponentConfig,
  ColorKeys,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';

type ColorDefaults = {
  [key: string]: { value: StyleValueColorConfig; label: string };
};

const DEFAULT_COLOR_ATTRIBUTES = {
  'background-color': {
    label: 'Background color',
  },
  'fill-color': {
    label: 'Fill color',
  },
  'stroke-color': {
    label: 'Stroke color',
  },
};

type ColorStylesProps = {
  componentPreview: ColorComponent;
  component: ColorComponentConfig;
  styleVariantIndex: number;
  editable: boolean;
  baseComponentConfig: ColorComponentConfig;
  submitStyle: (field: string, values: any) => void;
  hiddenFields?: string[];
};

const ColorStyles = ({
  componentPreview,
  component,
  styleVariantIndex,
  editable,
  baseComponentConfig,
  submitStyle,
  hiddenFields = [],
}: ColorStylesProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const renderable = Object.keys(baseComponentConfig['style-variants'][0]).filter(
    (key) => key in DEFAULT_COLOR_ATTRIBUTES && !hiddenFields.includes(key),
  );

  const colorDefaults: ColorDefaults = useMemo(() => {
    const writable: ColorDefaults = {};
    renderable.forEach((key: ColorKeys) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      writable[key] = {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...DEFAULT_COLOR_ATTRIBUTES[key],
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        value: componentPreview.styles?.[key]?.base?.base ?? {
          value: '',
          unit: 'hex',
        },
      };

      if (component.type === UiComponentConfigType.ImageLocal) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const colorElements = Object.keys(componentPreview?.styles?.[key] ?? {});

        if (colorElements.length > 1) {
          colorElements
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            .filter((elem) => elem !== 'base' && !hiddenFields.includes(`${key}--${elem}`))
            .map((elem) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              writable[`${key}--${elem}`] = {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                label: `${DEFAULT_COLOR_ATTRIBUTES[key].label} (${elem})`,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                value: componentPreview.styles?.[key]?.[elem]?.base ?? {
                  value: '',
                  unit: 'hex',
                },
              };
            });
        }
      }
    });
    return writable;
  }, [componentPreview.styles, renderable, component.type, hiddenFields]);

  const onChange = useCallback(
    (hex: string, field: string) => {
      submitStyle(field, { unit: 'hex', value: hex });
    },
    [submitStyle],
  );

  return (
    <SectionAccordion title='Colors'>
      {Object.entries(colorDefaults).map(([rootStyle, { label, value }]) => (
        <SectionInputWrapper key={`color-styles-root-${rootStyle}-${styleVariantIndex}`}>
          <ColorStyleInput
            label={label}
            field={rootStyle}
            name={component.name}
            value={value.value}
            editable={editable}
            onChange={onChange}
          />
        </SectionInputWrapper>
      ))}
    </SectionAccordion>
  );
};

export default ColorStyles;
